@ -1,463 +1,402 @@
<template>
    <v-container class="iris-code-matrix-container">
        <v-row justify="center">
            <v-col cols="12">
                <v-card class="status-overview-card mb-6">
                    <v-card-title class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                            <v-icon color="primary" size="24" class="mr-2">mdi-table-search</v-icon>
                            IRIS Code Matrix
                        </div>
                        <div class="d-flex align-center gap-2">
                            <v-select
                                v-model="printStyle"
                                :items="printStyleOptions"
                                item-title="text"
                                item-value="value"
                                variant="solo"
                                density="default"
                                color="primary"
                                class="print-style-select"
                                prepend-inner-icon="mdi-format-list-bulleted-type"
                                hide-details
                                placeholder="Druckstil wählen"
                            />
                            <v-btn
                                color="primary"
                                variant="tonal"
                                prepend-icon="mdi-printer"
                                @click="printMatrix"
                                class="action-btn"
                            >
                                Drucken
                            </v-btn>
                            <v-btn
                                color="success"
                                variant="tonal"
                                prepend-icon="mdi-file-pdf-box"
                                @click="exportToPdf"
                                class="action-btn"
                            >
                                Als PDF speichern
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-divider class="card-divider"></v-divider>
                    <v-card-text class="py-4">
                        <v-text-field
                            v-model="searchCode"
                            label="Fehlercode oder Bereich suchen (z.B. G07 oder Brühgruppe)"
                            variant="outlined"
                            bg-color="rgba(30, 30, 40, 0.5)"
                            class="form-field"
                            color="success"
                            clearable
                        />

                        <!-- Neue kompakte Darstellung -->
                        <div class="matrix-grid-container">
                            <div
                                v-for="item in filteredRows"
                                :key="item.code"
                                class="matrix-item-card"
                                :class="{ 'highlight': isHighlighted(item) }"
                            >
                                <div class="item-header">
                                    <span class="code-badge">{{ item.code }}</span>
                                    <span class="section-name">{{ item.section }}</span>
                                </div>
                                <div class="error-types-container">
                                    <div class="error-types-list">
                                        <span
                                            v-for="type in item.types"
                                            :key="type"
                                            class="error-type-pill"
                                            :class="{ 'clickable': isMobileDevice }"
                                            :title="type + ': ' + errorTypeLegend[type]"
                                            @click="isMobileDevice ? showCodeDetails(type, item.section) : null"
                                        >
                                            {{ type }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Kompakte Legende -->
                        <div class="legend-container mt-6">
                            <h3 class="legend-title">
                                <v-icon size="18" class="mr-2">mdi-information</v-icon>
                                Fehlerarten-Codes
                            </h3>
                            <div class="legend-compact-grid">
                                <div v-for="(desc, code) in errorTypeLegend" :key="code" class="legend-item">
                                    <span class="legend-code">{{ code }}</span>
                                    <span class="legend-text">{{ desc }}</span>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Mobile Code-Detail Dialog -->
        <v-dialog v-model="showCodeDialog" max-width="400" :fullscreen="false">
            <v-card class="code-detail-card">
                <v-card-title class="d-flex align-center pa-4">
                    <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                    Fehlercode Details
                    <v-spacer></v-spacer>
                    <v-btn icon variant="text" @click="showCodeDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text class="pa-4">
                    <div class="detail-content">
                        <div class="code-display">
                            <span class="code-badge-large">{{ selectedCode }}</span>
                        </div>

                        <div class="section-info mt-3">
                            <v-icon color="primary" size="20" class="mr-2">mdi-wrench</v-icon>
                            <span class="section-text">{{ selectedSection }}</span>
                        </div>

                        <div class="description-info mt-4">
                            <div class="description-label">
                                <v-icon color="success" size="20" class="mr-2">mdi-text-box-outline</v-icon>
                                Beschreibung:
                            </div>
                            <div class="description-text">
                                {{ selectedDescription }}
                            </div>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" variant="elevated" @click="showCodeDialog = false">
                        Verstanden
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface MatrixRow {
    section: string;
    code: string;
    types: string[];
}

const searchCode = ref('');
const printStyle = ref('table');

// Mobile Dialog für Fehlercode-Details
const showCodeDialog = ref(false);
const selectedCode = ref('');
const selectedDescription = ref('');
const selectedSection = ref('');

const printStyleOptions = [
    { text: 'Tabelle (klassisch)', value: 'table' },
    { text: 'Karten (modern)', value: 'cards' }
];

const matrixData: MatrixRow[] = [
    { section: 'Bedieneinheit', code: 'G07', types: ['1', '4', 'N'] },
    { section: 'Verdrahtung', code: 'G08', types: ['7', 'D'] },
    { section: 'Verbindung / Kontakt', code: 'G09', types: ['7', 'T'] },
    { section: 'Elektronik PCB', code: 'G12', types: ['1', 'N', 'Q'] },
    { section: 'Mikroschalter', code: 'G16', types: ['M1', 'N'] },
    { section: 'Brühgruppe', code: 'G18', types: ['A', 'D1', 'K', 'M1', 'N', '92'] },
    { section: 'Dichtung', code: 'G30', types: ['A', '92'] },
    { section: 'Wassersystem/Schläuche', code: 'G33', types: ['7', 'D', 'D1', 'M1', '92'] },
    { section: 'Filter', code: 'G34', types: ['9', 'K', 'M1'] },
    { section: 'Kondensor', code: 'G35', types: ['K', 'M1'] },
    { section: 'Flowmeter', code: 'G41', types: ['A', 'K', 'M1', 'N', '92'] },
    { section: 'Chassis/Bodenplatte/Gehäuse', code: 'G47', types: ['7', 'D', 'G1', 'G3', 'H1'] },
    { section: 'Potentiometer', code: 'G05', types: ['7', 'N'] },
    { section: 'Abtropfschale/Reste-Behälter', code: 'G55', types: ['9', 'A', 'D', 'G3', '92'] },
    { section: 'Falsche Benutzung', code: 'G61', types: ['4', '3'] },
    { section: 'Tank/Wasserversorgung', code: 'G70', types: ['D', 'K', 'N', '92'] },
    { section: 'Pumpe', code: 'G72', types: ['A', 'N'] },
    { section: 'Dampfdüse', code: 'G78', types: ['3', '7', '9', 'A', 'M1', 'N', '92'] },
    { section: 'Verteiler / Distributor', code: 'G80', types: ['A', 'K', 'M1', 'N'] },
    { section: 'Mahlwerk', code: 'G82', types: ['7', '9', 'A', 'C', 'M1', 'N'] },
    { section: 'Kaffeeauslauf', code: 'B01', types: ['4', 'A', 'D1', 'K', '92'] },
];

const errorTypeLegend: Record<string, string> = {
    '1': 'Software-Problem',
    '4': 'Kein Fehler feststellbar',
    '3': 'Kein Problem (Gerät innerhalb der Spez.)',
    '7': 'Falsch verdrahtet/zusammengebaut',
    '9': 'Fehlbedienung',
    'A': 'Abgenutzt/verschlissen oder mechanisch defekt',
    'C': 'Mechanische Fehleinstellung',
    'D': 'Abgetrennt/gebrochen',
    'D1': 'Riss/gerissen',
    'G1': 'Kratzer oder äußere Beschädigung',
    'G3': 'Scharfkantig',
    'H1': 'Rissig/abgeblättert',
    'K': 'Undicht',
    'M1': 'Verstopft/blockiert',
    'N': 'Elektrisches Bauteil defekt',
    'Q': 'Elektrischer Kurzschluss',
    'T': 'Schlechte Kontaktverbindung',
    '92': 'Schlechte Wartung',
};

const filteredRows = computed(() => {
    if (!searchCode.value) return matrixData;
    const search = searchCode.value.toLowerCase();
    return matrixData.filter((row) =>
        row.code.toLowerCase().includes(search) ||
        row.section.toLowerCase().includes(search)
    );
});

const isHighlighted = (item: MatrixRow) => {
    if (!searchCode.value) return false;
    const search = searchCode.value.toLowerCase();
    return item.code.toLowerCase().includes(search) ||
           item.section.toLowerCase().includes(search);
};

const generateCardsLayout = () => {
    let htmlContent = '<div style="font-family: Arial, sans-serif; color: #000; padding: 15mm; max-width: 180mm;">';
    htmlContent += '<h1 style="text-align: center; color: #2e7d32; margin: 0 0 15px 0; font-size: 20px; font-weight: bold;">IRIS Code Matrix</h1>';
    htmlContent += '<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 15px;">';

    matrixData.forEach(item => {
        htmlContent += '<div style="border: 1px solid #ddd; padding: 8px; background: #f9f9f9; border-radius: 4px;">';
        htmlContent += '<div style="display: flex; align-items: center; margin-bottom: 4px;">';
        htmlContent += `<span style="background: #2e7d32; color: white; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 11px; margin-right: 8px;">${item.code}</span>`;
        htmlContent += `<span style="font-size: 10px; font-weight: bold;">${item.section}</span>`;
        htmlContent += '</div>';
        htmlContent += '<div style="display: flex; flex-wrap: wrap; gap: 2px;">';
        item.types.forEach(type => {
            htmlContent += `<span style="background: #4caf50; color: white; padding: 1px 5px; border-radius: 3px; font-size: 9px; font-weight: bold;">${type}</span>`;
        });
        htmlContent += '</div>';
        htmlContent += '</div>';
    });

    htmlContent += '</div>';
    htmlContent += '<div style="border-top: 2px solid #2e7d32; padding-top: 10px;">';
    htmlContent += '<h2 style="font-size: 14px; color: #2e7d32; margin: 0 0 8px 0; font-weight: bold;">Fehlerarten-Codes</h2>';
    htmlContent += '<div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; font-size: 8px;">';

    Object.entries(errorTypeLegend).forEach(([code, desc]) => {
        htmlContent += '<div style="display: flex; align-items: flex-start;">';
        htmlContent += `<span style="background: #2e7d32; color: white; padding: 1px 4px; border-radius: 2px; font-weight: bold; margin-right: 4px; min-width: 18px; text-align: center;">${code}</span>`;
        htmlContent += `<span style="line-height: 1.2;">${desc}</span>`;
        htmlContent += '</div>';
    });

    htmlContent += '</div>';
    htmlContent += '</div>';
    htmlContent += '</div>';

    return htmlContent;
};

const generateTableLayout = () => {
    const errorTypes = ['1', '4', '3', '7', '9', 'A', 'C', 'D', 'D1', 'G1', 'G3', 'H1', 'K', 'M1', 'N', 'Q', 'T', '92'];

    let htmlContent = '<div style="font-family: Arial, sans-serif; color: #000; padding: 10mm; max-width: 277mm;">';
    htmlContent += '<h1 style="text-align: center; color: #2e7d32; margin: 0 0 10px 0; font-size: 18px; font-weight: bold;">IRIS Code Matrix</h1>';

    // Matrix-Tabelle
    htmlContent += '<table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 9px;">';

    // Tabellen-Header
    htmlContent += '<thead>';
    htmlContent += '<tr>';
    htmlContent += '<th style="border: 1px solid #333; padding: 3px; background-color: #e8f5e9; font-weight: bold; text-align: left; width: 120px;">Bereich</th>';
    htmlContent += '<th style="border: 1px solid #333; padding: 3px; background-color: #e8f5e9; font-weight: bold; text-align: center; width: 35px;">Code</th>';

    errorTypes.forEach(type => {
        htmlContent += `<th style="border: 1px solid #333; padding: 3px; background-color: #e8f5e9; font-weight: bold; text-align: center; width: 20px; font-size: 8px;">${type}</th>`;
    });

    htmlContent += '</tr>';
    htmlContent += '</thead>';

    // Tabellen-Body
    htmlContent += '<tbody>';

    matrixData.forEach((row, idx) => {
        const bgColor = idx % 2 === 0 ? '#fff' : '#f9f9f9';
        htmlContent += `<tr style="background-color: ${bgColor}">`;
        htmlContent += `<td style="border: 1px solid #333; padding: 3px; text-align: left; font-weight: bold; font-size: 8px;">${row.section}</td>`;
        htmlContent += `<td style="border: 1px solid #333; padding: 3px; text-align: center; font-weight: bold; font-size: 8px;">${row.code}</td>`;

        errorTypes.forEach(type => {
            if (row.types.includes(type)) {
                htmlContent += '<td style="border: 1px solid #333; padding: 2px; text-align: center;">';
                htmlContent += '<span style="display: inline-block; background-color: #4caf50; color: white; font-weight: bold; border-radius: 50%; width: 14px; height: 14px; line-height: 14px; text-align: center; font-size: 8px;">X</span>';
                htmlContent += '</td>';
            } else {
                htmlContent += '<td style="border: 1px solid #333; padding: 2px; text-align: center;"></td>';
            }
        });

        htmlContent += '</tr>';
    });

    htmlContent += '</tbody>';
    htmlContent += '</table>';

    // Legende
    htmlContent += '<div style="border-top: 2px solid #2e7d32; padding-top: 8px;">';
    htmlContent += '<h2 style="font-size: 12px; color: #2e7d32; margin: 0 0 6px 0; font-weight: bold;">Legende Fehlerarten</h2>';
    htmlContent += '<div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px; font-size: 7px;">';

    Object.entries(errorTypeLegend).forEach(([code, desc]) => {
        htmlContent += '<div style="display: flex; align-items: flex-start; margin-bottom: 1px;">';
        htmlContent += `<span style="background: #2e7d32; color: white; padding: 1px 3px; border-radius: 2px; font-weight: bold; margin-right: 3px; min-width: 16px; text-align: center; font-size: 7px;">${code}</span>`;
        htmlContent += `<span style="line-height: 1.1; font-size: 7px;">${desc}</span>`;
        htmlContent += '</div>';
    });

    htmlContent += '</div>';
    htmlContent += '</div>';
    htmlContent += '</div>';

    return htmlContent;
};

const printMatrix = async () => {
    const printContainer = document.createElement('div');
    printContainer.style.position = 'absolute';
    printContainer.style.top = '-9999px';
    printContainer.style.left = '-9999px';
    printContainer.style.backgroundColor = 'white';

    // Wähle Layout basierend auf dem ausgewählten Stil
    let htmlContent: string;
    let containerWidth: string;
    let pageSettings: string;

    if (printStyle.value === 'cards') {
        htmlContent = generateCardsLayout();
        containerWidth = '210mm';
        pageSettings = '@page { size: A4 portrait; margin: 0.5cm; } body { zoom: 0.9; }';
    } else {
        htmlContent = generateTableLayout();
        containerWidth = '297mm';
        pageSettings = '@page { size: A4 landscape; margin: 0.5cm; } body { zoom: 0.85; }';
    }

    printContainer.style.width = containerWidth;
    printContainer.innerHTML = htmlContent;
    document.body.appendChild(printContainer);

    try {
        const canvas = await html2canvas(printContainer, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: 'white'
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) throw new Error("Couldn't open print window");

        printWindow.document.write('<!DOCTYPE html>');
        printWindow.document.write('<html><head><title>IRIS Code Matrix - Druckansicht</title>');
        printWindow.document.write(`<style>body { margin: 0; padding: 0; } img { max-width: 100%; height: auto; } @media print { ${pageSettings} }</style>`);
        printWindow.document.write('</head><body>');
        printWindow.document.write('<img src="' + canvas.toDataURL('image/png') + '" />');
        printWindow.document.write('<' + 'script>');
        printWindow.document.write('window.onload = function() { window.print(); setTimeout(() => { window.close(); }, 500); };');
        printWindow.document.write('</' + 'script>');
        printWindow.document.write('</body></html>');

        printWindow.document.close();
    } catch (error) {
        console.error("Error generating print view:", error);
    } finally {
        document.body.removeChild(printContainer);
    }
};

const exportToPdf = async () => {
    const pdfContainer = document.createElement('div');
    pdfContainer.style.position = 'absolute';
    pdfContainer.style.top = '-9999px';
    pdfContainer.style.left = '-9999px';
    pdfContainer.style.backgroundColor = 'white';

    // Wähle Layout basierend auf dem ausgewählten Stil
    let htmlContent: string;
    let containerWidth: string;
    let orientation: 'portrait' | 'landscape';
    let imgWidth: number;
    let maxHeight: number;

    if (printStyle.value === 'cards') {
        htmlContent = generateCardsLayout();
        containerWidth = '210mm';
        orientation = 'portrait';
        imgWidth = 200;
        maxHeight = 287;
    } else {
        htmlContent = generateTableLayout();
        containerWidth = '297mm';
        orientation = 'landscape';
        imgWidth = 287;
        maxHeight = 200;
    }

    pdfContainer.style.width = containerWidth;
    pdfContainer.innerHTML = htmlContent;
    document.body.appendChild(pdfContainer);

    try {
        const canvas = await html2canvas(pdfContainer, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: 'white'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: 'a4'
        });

        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight <= maxHeight) {
            pdf.addImage(imgData, 'PNG', 5, 5, imgWidth, imgHeight);
        } else {
            pdf.addImage(imgData, 'PNG', 5, 5, imgWidth, maxHeight);
        }

        pdf.save('iris-code-matrix.pdf');

    } catch (error) {
        console.error("Error generating PDF:", error);
    } finally {
        document.body.removeChild(pdfContainer);
    }
};

// Mobile-optimierte Code-Detail-Anzeige
const showCodeDetails = (code: string, section: string) => {
    selectedCode.value = code;
    selectedDescription.value = errorTypeLegend[code] || 'Beschreibung nicht verfügbar';
    selectedSection.value = section;
    showCodeDialog.value = true;
};

// Prüfung ob Mobile Device
const isMobileDevice = computed(() => {
    return window.innerWidth <= 768;
});
</script>

<style scoped>
.iris-code-matrix-container {
    padding-top: 32px;
}

.matrix-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 16px;
    margin-top: 24px;
    max-width: 100%;
}

.matrix-item-card {
    background: rgba(30, 30, 40, 0.85);
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 12px;
    padding: 16px;
    transition: all 0.3s ease;
    color: #fff;
}

.matrix-item-card:hover {
    border-color: rgba(76, 175, 80, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2);
}

.matrix-item-card.highlight {
    border-color: #4caf50;
    background: rgba(76, 175, 80, 0.15);
    box-shadow: 0 0 16px rgba(76, 175, 80, 0.3);
}

.item-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    gap: 12px;
}

.code-badge {
    background: linear-gradient(135deg, #43a047, #66bb6a);
    color: white;
    padding: 6px 12px;
    border-radius: 8px;
    font-weight: bold;
    font-size: 14px;
    letter-spacing: 0.5px;
    min-width: 50px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(67, 160, 71, 0.3);
}

.section-name {
    font-size: 14px;
    font-weight: 600;
    color: #e8f5e9;
    flex: 1;
}

.error-types-container {
    margin-top: 8px;
}

.error-types-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.error-type-pill {
    background: linear-gradient(135deg, #2e7d32, #388e3c);
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: help;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(46, 125, 50, 0.3);
}

.error-type-pill:hover {
    background: linear-gradient(135deg, #388e3c, #43a047);
    transform: scale(1.05);
}

.legend-container {
    background: rgba(30, 30, 40, 0.9);
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 12px;
    margin-top: 32px;
    padding: 20px 24px;
    color: #fff;
    max-width: 100%;
}

.legend-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #a5d6a7;
    display: flex;
    align-items: center;
}

.legend-compact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 8px 16px;
}

.legend-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 4px;
}

.legend-code {
    background: linear-gradient(135deg, #2e7d32, #388e3c);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 11px;
    min-width: 24px;
    text-align: center;
    flex-shrink: 0;
}

.legend-text {
    color: #e8f5e9;
    font-size: 12px;
    line-height: 1.3;
    opacity: 0.9;
}

.gap-2 {
    gap: 8px;
}

.print-style-select {
    min-width: 170px;
    max-width: 170px;
}

.print-style-select :deep(.v-field) {
    background: rgba(13, 110, 253, 0.08) !important;
    border: 1px solid rgba(13, 110, 253, 0.12) !important;
    border-radius: 8px !important;
    height: 36px !important;
    min-height: 36px !important;
    box-shadow: none !important;
}

.print-style-select :deep(.v-field:hover) {
    background: rgba(13, 110, 253, 0.12) !important;
    border-color: rgba(13, 110, 253, 0.25) !important;
}

.print-style-select :deep(.v-field--focused) {
    background: rgba(13, 110, 253, 0.08) !important;
    border-color: #0d6efd !important;
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.15) !important;
}

.print-style-select :deep(.v-field__input) {
    color: rgba(255, 255, 255, 0.87) !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    min-height: 20px !important;
    line-height: 1.2 !important;
    align-items: center !important;
}

.print-style-select :deep(.v-field__prepend-inner) {
    color: rgba(13, 110, 253, 0.8) !important;
    padding-top: 8px !important;
    margin-right: 8px !important;
}

.print-style-select :deep(.v-field__append-inner) {
    color: rgba(13, 110, 253, 0.8) !important;
    padding-top: 8px !important;
}

.print-style-select :deep(.v-select__selection) {
    color: rgba(255, 255, 255, 0.87) !important;
    font-weight: 500 !important;
}

.print-style-select :deep(.v-field__placeholder) {
    color: rgba(255, 255, 255, 0.6) !important;
    font-weight: 500 !important;
}

.print-style-select :deep(.v-input__control) {
    min-height: 36px !important;
    height: 36px !important;
}

.action-btn {
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0.25px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .matrix-grid-container {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .matrix-item-card {
        padding: 12px;
    }

    .item-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .legend-compact-grid {
        grid-template-columns: 1fr;
    }
}

/* Print styles */
@media print {
    .iris-code-matrix-container {
        padding: 0;
    }

    .matrix-grid-container {
        display: block;
    }

    .matrix-item-card {
        break-inside: avoid;
        margin-bottom: 8px;
        background: #f9f9f9 !important;
        color: #000 !important;
        border: 1px solid #ddd !important;
    }

    .code-badge {
        background: #2e7d32 !important;
        color: white !important;
    }

    .section-name {
        color: #000 !important;
    }

    .error-type-pill {
        background: #4caf50 !important;
        color: white !important;
    }

    .legend-container {
        background: #f5f5f5 !important;
        color: #000 !important;
        border: 1px solid #ddd !important;
    }

    .legend-title {
        color: #2e7d32 !important;
    }

    .legend-code {
        background: #2e7d32 !important;
        color: white !important;
    }

    .legend-text {
        color: #000 !important;
    }
}

/* Mobile Code Dialog Styles */
.code-detail-card {
    background: linear-gradient(135deg, rgba(30, 30, 40, 0.95) 0%, rgba(40, 40, 50, 0.95) 100%) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(76, 175, 80, 0.3);
    color: #fff;
}

.code-badge-large {
    background: linear-gradient(90deg, #43a047 60%, #66bb6a 100%);
    color: #fff;
    font-weight: 700;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 1.2rem;
    box-shadow: 0 4px 8px rgba(67, 160, 71, 0.3);
    letter-spacing: 0.05em;
    display: inline-block;
}

.section-text {
    font-weight: 600;
    color: #fff;
    font-size: 1rem;
}

.description-label {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

.description-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    line-height: 1.4;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    border-left: 4px solid #43a047;
}

.detail-content {
    text-align: center;
}

.section-info {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Clickable Pills für Mobile */
.error-type-pill.clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.error-type-pill.clickable:hover {
    background: linear-gradient(90deg, #388e3c 60%, #43a047 100%);
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(67, 160, 71, 0.4);
}

.error-type-pill.clickable:active {
    transform: scale(0.98);
}

/* Mobile-specific improvements */
@media (max-width: 768px) {
    .error-type-pill.clickable::after {
        content: " 👆";
        opacity: 0.6;
        font-size: 0.8em;
    }
}
</style>
